
import clsx from 'clsx';
import React, { ReactElement, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keycloak } from '../../../lib/keycloak/index.ts';
import { useAppDispatch, useAppSelector } from '../../../lib/redux/hook.ts';
import { findChatBot, setFindMessagesStatus, setSelectedChat } from '../../../pages/company/chat/slice.ts';
import { SubscriptionTierLimit } from '../../../pages/financialAdvisor/dto.ts';
import { colors } from '../../colors.ts';
import { BotIcon } from '../../icons/botIcon.tsx';
import { BreadcrumbsIcon } from '../../icons/breadcrumbIcon.tsx';
import { BrightySecondaryIcon } from '../../icons/brightySecondaryIcon.tsx';
import { CalendarIcon } from '../../icons/calendarIcon.tsx';
import { ChatIcon } from '../../icons/chatIcon.tsx';
import { CompanyIcon } from '../../icons/companyIcon.tsx';
import { DashboardIcon } from '../../icons/dashboardIcon.tsx';
import { DocumentIcon } from '../../icons/documentIcon.tsx';
import { FinancialAdvisorIcon } from '../../icons/financialAdvisorIcon.tsx';
import { LogoIcon } from '../../icons/logoIcon.tsx';
import { NoticeIcon } from '../../icons/noticeIcon.tsx';
import { OperatorIcon } from '../../icons/operatorIcon.tsx';
import { SettingIcon } from '../../icons/settingIcon.tsx';
import { MenuStatus } from '../../layout/dto.ts';
import { setMenuStatus } from '../../layout/slice.ts';
import Avatar from '../../molecules/avatar/index.tsx';
import { Pills } from '../../molecules/pills/index.tsx';
import { MenuItems } from './dto.ts';
import NavLinks from './nav-links.tsx';
import { BrightyIcon } from '../../icons/brightyIcon.tsx';

const uriMap = new Map<MenuItems, string>([
  [MenuItems.DASHBOARD, '/'],
  [MenuItems.COMPANY, '/company'],
  [MenuItems.FINANCIAL_ADVISOR, '/financial-advisor'],
  [MenuItems.OPERATOR, '/operator'],
  [MenuItems.COLLABORATOR, '/collaborator'],
  [MenuItems.NOTICE, '/notice'],
  [MenuItems.DOCUMENT, '/document-company'],
  [MenuItems.CHAT, '/message'],
  [MenuItems.BRIGHTY, '/brighty'],
  [MenuItems.CALENDAR, '/calendar'],
  [MenuItems.SETTING, '/setting'],
  [MenuItems.APPOINTMENTS, '/appointments']
])

const labelMap = new Map<MenuItems, string>([
  [MenuItems.DASHBOARD, 'Dashboard'],
  [MenuItems.COMPANY, 'Aziende'],
  [MenuItems.FINANCIAL_ADVISOR, 'Commercialisti'],
  [MenuItems.OPERATOR, 'Operatori'],
  [MenuItems.COLLABORATOR, 'Collaboratori'],
  [MenuItems.NOTICE, 'Avvisi'],
  [MenuItems.DOCUMENT, 'Documenti'],
  [MenuItems.CHAT, 'Messaggi'],
  [MenuItems.BRIGHTY, 'Brighty'],
  [MenuItems.CALENDAR, 'Calendario'],
  [MenuItems.SETTING, 'Impostazioni'],
  [MenuItems.APPOINTMENTS, 'Appuntamenti']
])

const iconMap = new Map<MenuItems, ReactElement>([
  [MenuItems.DASHBOARD, <DashboardIcon size={"20"} color={''} />],
  [MenuItems.COMPANY, <CompanyIcon size={'20'} color={''} />],
  [MenuItems.FINANCIAL_ADVISOR, <FinancialAdvisorIcon size='20' />],
  [MenuItems.OPERATOR, <OperatorIcon size={"20"} color={''} />],
  [MenuItems.COLLABORATOR, <OperatorIcon size={"20"} color={''} />],
  [MenuItems.NOTICE, <NoticeIcon size={'20'} color={''} />],
  [MenuItems.DOCUMENT, <DocumentIcon size={'20'} color={''} />],
  [MenuItems.BRIGHTY, <BrightyIcon size={'20'} color={''} />],
  [MenuItems.CHAT, <ChatIcon size={'20'} color={''} />],
  [MenuItems.CALENDAR, <CalendarIcon size={'20'} color={''} />],
  [MenuItems.SETTING, <SettingIcon color={''} size={'20'} />],
  [MenuItems.APPOINTMENTS, <CalendarIcon size={'20'} color={''} />]
])

export default function SidebarComponent(props: { menuItem: MenuItems }) {
  const { menuItem } = props

  const layoutState = useAppSelector(state => state.layout)
  const financialAdvisorState = useAppSelector(state => state.financialAdvisor)
  const documentState = useAppSelector(state => state.document)
  const chatState = useAppSelector(state => state.chat)
  const objectState = useAppSelector(state => state.object)
  const dispatch = useAppDispatch()
  const authState = useAppSelector(state => state.auth)

  const faName = financialAdvisorState.findByIdFinancialAdvisorResponse ? financialAdvisorState.findByIdFinancialAdvisorResponse?.commercialName.trim() ? financialAdvisorState.findByIdFinancialAdvisorResponse?.commercialName! : (financialAdvisorState.findByIdFinancialAdvisorResponse.name + " " + financialAdvisorState.findByIdFinancialAdvisorResponse.surname) : ''

  let logo: ReactNode[] = []
  let counterDocuments: ReactNode
  let counterMessages: ReactNode

  if (!keycloak.hasRealmRole("company") && !keycloak.hasRealmRole("administrative_company")) {
    if (layoutState.menuStatus === MenuStatus.CLOSED) {
      logo.push(
        <LogoIcon key={'2001'} />
      )
    } else {
      logo.push(
        <div className=' flex flex-row items-center gap-2'>
          <LogoIcon key={'2001'} />
          <svg width="104" height="22" viewBox="0 0 104 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M99.2211 3.26841V6.75389H97.3194V8.45902H99.2211V14.2822C99.2211 16.2834 100.371 17.3859 102.461 17.3859H103.779V15.6807H102.461C101.61 15.6807 101.178 15.2106 101.178 14.2822V8.45902H103.954V6.75389H101.178V3.26785L99.2211 3.26841ZM72.7609 4.2301C72.7609 4.78602 72.3474 5.20521 71.7993 5.20521C71.2521 5.20521 70.8237 4.77709 70.8237 4.2301C70.8237 3.68206 71.2431 3.26827 71.7993 3.26827C72.3474 3.26827 72.7609 3.68206 72.7609 4.2301ZM59.7931 12.0113C59.7931 14.2743 58.5476 15.8552 56.7643 15.8552C54.8897 15.8552 53.5805 14.2426 53.5805 11.9338C53.5805 9.77341 54.8816 8.26437 56.745 8.26437C58.5681 8.26437 59.7931 9.77022 59.7931 12.0113ZM51.623 3.27561V17.3853H53.2356L53.3774 15.7932C54.1805 16.9421 55.5009 17.5989 57.0166 17.5989C59.8381 17.5989 61.8084 15.3013 61.8084 12.0113C61.8084 8.77843 59.8381 6.52063 57.0166 6.52063C55.637 6.52063 54.4206 7.05427 53.5805 8.02592V3.27561H51.623ZM49.2151 3.27616V17.3858H47.258V3.27616H49.2151ZM32.2413 10.9126C31.9776 9.25443 30.8517 8.26437 29.2299 8.26437C27.2719 8.26437 26.007 9.73507 26.007 12.0113C26.007 13.9246 27.0395 15.8549 29.3464 15.8549C30.9076 15.8549 31.9628 14.8752 32.2413 13.1672L32.265 13.0219H34.1774L34.1563 13.2139C33.856 15.9598 32.0505 17.5989 29.3267 17.5989C26.1587 17.5989 24.0304 15.3535 24.0304 12.0113C24.0304 8.72709 26.1353 6.52035 29.2685 6.52035C31.9882 6.52035 33.8611 8.18598 34.1563 10.8667L34.1777 11.059H32.2644L32.2413 10.9126ZM18.4 10.9354L19.7963 11.401C21.8241 12.0697 22.9228 12.7674 22.9228 14.5345C22.9228 16.4824 21.4662 17.5996 18.9265 17.5996C16.2945 17.5996 14.5051 16.1797 14.3676 13.9827L14.3561 13.7984H16.2713L16.2849 13.9567C16.3864 15.1221 17.4538 15.8752 19.004 15.8752C20.3185 15.8752 20.9848 15.3977 20.9848 14.4569C20.9848 13.6546 20.3966 13.4524 19.4219 13.1177L17.6948 12.5338C16.1924 12.0342 14.6389 11.5176 14.6389 9.5665C14.6389 7.6881 16.0886 6.52098 18.422 6.52098C20.8406 6.52098 22.4359 7.89196 22.6887 10.1881L22.71 10.3801H20.7913L20.7726 10.2282C20.6172 8.94948 19.7617 8.24541 18.3637 8.24541C17.2205 8.24541 16.5381 8.72487 16.5381 9.52789C16.5381 10.3406 17.3226 10.5903 18.0812 10.8316L18.0909 10.8351C18.1962 10.8682 18.3 10.9014 18.4 10.9354ZM42.7176 12.1221V13.0593C42.7176 14.6829 41.4561 15.8165 39.6501 15.8165C38.27 15.8165 37.3782 15.1992 37.3782 14.2432C37.3782 13.1285 38.5389 12.9129 40.0566 12.7466L40.1028 12.7416C41.1949 12.6243 42.1426 12.5222 42.7176 12.1221ZM44.636 15.3881V10.3814C44.636 8.07235 42.8897 6.5209 40.2905 6.5209C37.7642 6.5209 35.8579 7.99762 35.6546 10.1125L35.6362 10.3025H37.5685L37.5895 10.1534C37.7504 9.03009 38.8451 8.24527 40.2516 8.24527C41.7084 8.24527 42.6994 9.06352 42.7176 10.2803C42.7051 10.8163 41.893 10.9122 40.5684 11.0244L40.5119 11.0293L40.4851 11.0316C40.3026 11.0473 40.115 11.0634 39.9231 11.0827C37.5849 11.3251 35.4208 11.8804 35.4208 14.2821C35.4208 16.2617 36.9895 17.5407 39.4172 17.5407C40.9205 17.5407 42.0919 17.02 42.8118 16.0333C42.922 16.9063 43.5735 17.3855 44.6568 17.3855H45.7423V15.7194H44.9869C44.7343 15.7194 44.636 15.6265 44.636 15.3881ZM65.1901 6.78474L65.344 8.3391C66.076 7.26337 67.2668 6.69542 68.7946 6.69542H69.3173V8.63318H68.7946C66.7219 8.63318 65.5331 9.79368 65.5331 11.817V17.3853H63.5761V6.75361H65.1869L65.1901 6.78474ZM72.7711 6.75382H70.814V17.3855H72.7711V6.75382ZM82.7676 11.8563C82.7676 13.9824 81.4823 15.4672 79.6417 15.4672C77.8238 15.4672 76.5546 13.9985 76.5546 11.8952C76.5546 9.7577 77.8238 8.26478 79.6417 8.26478C81.4823 8.26478 82.7676 9.74185 82.7676 11.8563ZM82.9714 8.20396C82.1739 7.13343 80.8814 6.52104 79.409 6.52104C76.587 6.52104 74.5393 8.78922 74.5393 11.9146C74.5393 15.033 76.5417 17.2112 79.409 17.2112C80.7667 17.2112 81.9554 16.7035 82.7676 15.7798V16.9408C82.7676 18.7281 81.6282 19.7952 79.7195 19.7952C78.0542 19.7952 76.8458 18.9619 76.8458 17.8141V17.6409H74.8887V17.8141C74.8887 20.0532 76.785 21.5 79.7195 21.5C82.795 21.5 84.7054 19.7531 84.7054 16.9408V6.75396H83.1136L82.9714 8.20396ZM87.055 17.3857V3.27602H89.0122V7.79398C89.828 6.98376 90.9975 6.52104 92.235 6.52104C94.5801 6.52104 96.0955 8.04391 96.0955 10.4009V17.3857H94.1384V10.4591C94.1384 9.11763 93.2899 8.28436 91.9244 8.28436C90.2023 8.28436 89.032 9.3621 89.0122 10.966V17.3857H87.055ZM12.608 4.2301C12.608 4.78602 12.1945 5.20521 11.6464 5.20521C11.0995 5.20521 10.671 4.77709 10.671 4.2301C10.671 3.68206 11.0903 3.26827 11.6464 3.26827C12.1945 3.26827 12.608 3.68206 12.608 4.2301ZM0 17.3637H1.90719V10.9394H8.95171V9.16773H1.90719V5.03983H9.53406V3.26813H0V17.3637ZM10.6611 6.77112H12.6181V17.4028H10.6611V6.77112Z" fill="#1C1E4F" />
          </svg>

        </div>
      )
    }
  } else {
    if (layoutState.menuStatus === MenuStatus.CLOSED) {
      logo.push(
        <Avatar key={'2001'}
          type="single"
          size="md"
          shape="circle"
          imageUrl={objectState.findAvatarFinancialResponse!}
          altTextInitials={financialAdvisorState.findByIdFinancialAdvisorResponse ? financialAdvisorState.findByIdFinancialAdvisorResponse?.name.trim() ? (financialAdvisorState.findByIdFinancialAdvisorResponse?.name[0] || '') + (financialAdvisorState.findByIdFinancialAdvisorResponse?.surname[0] || '') : financialAdvisorState.findByIdFinancialAdvisorResponse?.commercialName[0] : ''}
        />
      )
    } else {
      logo.push(
        <Avatar key={'2001'}
          type="info"
          size="sm"
          firstRow={!faName.includes(' ') ? faName.length <= 15 ? faName : faName.substring(0, 15).concat('...') : faName.length <= 30 ? faName : faName.substring(0, 30).concat('...')}
          imageUrl={objectState.findAvatarFinancialResponse!}
          altTextInitials={financialAdvisorState.findByIdFinancialAdvisorResponse ? financialAdvisorState.findByIdFinancialAdvisorResponse?.name.trim() ? (financialAdvisorState.findByIdFinancialAdvisorResponse?.name[0] || '') + (financialAdvisorState.findByIdFinancialAdvisorResponse?.surname[0] || '') : financialAdvisorState.findByIdFinancialAdvisorResponse?.commercialName[0] : ''}
        />
      )
    }
  }

  if ((keycloak.hasRealmRole('company') || keycloak.hasRealmRole('administrative_company')) && layoutState.menuStatus !== MenuStatus.CLOSED) {
    if ((documentState.documentReportReponse && Object.values(documentState.documentReportReponse).reduce
      ((acc, current) => acc + current.unreads, 0) > 0))
      counterDocuments = <Pills size={'xs'} label={documentState.documentReportReponse && Object.values(documentState.documentReportReponse).reduce
        ((acc, current) => acc + current.unreads, 0) > 0 ? Object.values(documentState.documentReportReponse).reduce
        ((acc, current) => acc + current.unreads, 0) : undefined} outline={false} emphasis={true} color={'red'} />
    if (chatState.unSeenMessagesResponse && chatState.unSeenMessagesResponse[0] && chatState.unSeenMessagesResponse[0].unseen > 0)
      counterMessages = <Pills size={'xs'} label={chatState.unSeenMessagesResponse[0].unseen.toString()} outline={false} emphasis={true} color={'red'} />
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: colors.neutral[100], overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}
        className={clsx(
          'h-[64px] lg:h-[80px]',
          {
            'lg:py-[16px] lg:px-[20px] py-[16px] px-[24px] justify-between': layoutState.menuStatus !== MenuStatus.CLOSED,
            'py-[16px] lg:px-[0px] justify-center': layoutState.menuStatus === MenuStatus.CLOSED,
          }
        )}>
        {logo}
        <div
          onClick={() => {
            if (layoutState.menuStatus === MenuStatus.OPEN) {
              dispatch(setMenuStatus(MenuStatus.CLOSED))
            } else if (layoutState.menuStatus === MenuStatus.HOVER) {
              dispatch(setMenuStatus(MenuStatus.OPEN))
            }
          }}
          className={
            clsx(
              'transition-all cursor-pointer hidden',
              {
                'rotate-0 lg:block': layoutState.menuStatus === MenuStatus.OPEN,
                'rotate-180 lg:block': layoutState.menuStatus === MenuStatus.HOVER,
                'hidden': layoutState.menuStatus === MenuStatus.CLOSED
              }
            )
          }>
          <BreadcrumbsIcon size='20' color={'#7171B3'} />
        </div>
      </div>
      {
        layoutState.menuItems.map((item) => (
          <div key={labelMap.get(item)} className={"flex flex-col items-start justify-start px-4 py-1"}>
            <NavLinks
              counter={((labelMap.get(item) === 'Documenti' || labelMap.get(item) === 'Messaggi')) ? labelMap.get(item) === 'Documenti' ?
                counterDocuments
                :
                counterMessages : (labelMap.get(item) === 'Brighty' && keycloak.hasRealmRole('financial_advisor')) ?
                <div style={{backgroundColor: layoutState.theme[500]}} className={layoutState.menuStatus === MenuStatus.CLOSED ? 'hidden' : ' border-white border p-1 rounded-xl w-10 flex items-center justify-center'}><span className=' text-[10px] leading-4 text-white'>PRO</span></div> : <></>}
              menuItem={menuItem}
              link={
                {
                  href: uriMap.get(item),
                  name: labelMap.get(item),
                  icon: iconMap.get(item),
                  menuItem: item
                }
              }
            />
          </div>
        ))
      }
      <div
        style={{ padding: '24px 16px 24px 16px', height: '100%', display: 'flex', justifyContent: 'start', alignItems: 'flex-end' }}
        className='text-label-sm font-medium text-neutral-500 transition-all'
      >
        <span
          className={
            clsx(
              'transition-all',
              {
                "block": layoutState.menuStatus === MenuStatus.CLOSED,
                "hidden content-['© 2024 Fiscalbright']": layoutState.menuStatus !== MenuStatus.CLOSED
              }
            )
          }>
          ©
        </span>
      </div>
    </div>
  );
}
